import { useApolloClient, gql, TypedDocumentNode } from '@apollo/client'
import { OPTIMISTIC_CART_ID } from '@emico-hooks/cart'
import { useCreateCart } from '@emico-hooks/cart-create'
import { cartFragment } from '@emico-hooks/cart-fragment'
import { handleCartError } from '@emico-hooks/cart-handle-error'
import { useCartId, useCartIdContext, useSetCartId } from '@emico-hooks/cart-id'
import { useAuthorizationContext } from '@emico-hooks/login-token'
import { useCallback } from 'react'

import {
  AddToAndFetchCartMutation,
  AddToAndFetchCartMutationVariables,
  AddToCartMutation,
  AddToCartMutationVariables,
} from './useAddToCart.generated'

interface EnteredOptionInput {
  uid: string
  value: string
}

interface CartItemInput {
  /** Sku of the product */
  sku: string
  /** Amount of products to add */
  quantity: number
  /** For child products, the SKU of its parent product */
  parent_sku?: string | null
  /** An array of entered options for the base product, such as personalization text */
  entered_options?: Array<EnteredOptionInput | null> | null
  /**
   * The selected options for the base product, such as color or size with unique
   * ID for a `CustomizableRadioOption`, `CustomizableDropDownOption`,
   * `ConfigurableProductOptionsValues`, etc. objects
   */
  selected_options?: Array<string | null> | null
}

const addToCartMutation = gql`
  mutation AddToCart($cartId: String!, $cartItems: [CartItemInput!]!) {
    addProductsToCart(cartId: $cartId, cartItems: $cartItems) {
      userErrors {
        code
        message
      }
    }
  }
` as TypedDocumentNode<AddToCartMutation, AddToCartMutationVariables>

const addToAndFetchCartMutation = gql`
  mutation AddToAndFetchCart($cartId: String!, $cartItems: [CartItemInput!]!) {
    addProductsToCart(cartId: $cartId, cartItems: $cartItems) {
      cart {
        ...CartFragment
      }

      userErrors {
        code
        message
      }
    }
  }

  ${cartFragment}
` as TypedDocumentNode<
  AddToAndFetchCartMutation,
  AddToAndFetchCartMutationVariables
>

/**
 * Thrown when no cart could be created
 */
export class AddToCartNoCartError extends Error {}

/**
 * Adds items to the cart
 * @param fetchCart Set to true to also request the cart contents in the mutation
 */
export function useAddToCart(
  fetchCart: false,
): (
  cartItems: CartItemInput | CartItemInput[],
) => Promise<AddToCartMutation | undefined | null>
/**
 * Adds items to the cart
 */
export function useAddToCart(): (
  cartItems: CartItemInput | CartItemInput[],
) => Promise<AddToCartMutation | undefined | null>
/**
 * Adds items to the cart and fetches the cart contents
 * @param fetchCart Set to true to also request the cart contents in the mutation
 */
export function useAddToCart(
  fetchCart: true,
): (
  cartItems: CartItemInput | CartItemInput[],
) => Promise<AddToAndFetchCartMutation | undefined | null>
export function useAddToCart(
  fetchCart: boolean = false,
): (
  cartItems: CartItemInput | CartItemInput[],
) => Promise<AddToCartMutation | AddToAndFetchCartMutation | undefined | null> {
  const client = useApolloClient()
  const createCart = useCreateCart()
  const cartId = useCartId()
  const cartIdContext = useCartIdContext()
  const setCartId = useSetCartId()
  const authorizationContext = useAuthorizationContext()

  return useCallback(
    async (cartItems: CartItemInput | CartItemInput[]) => {
      // If user does not have a cart yet, we have to create one first.
      // Cart is not created until user adds an item to the cart,
      // to avoid creating unnecessary carts.
      // This is especially important to avoid GoogleBot from creating hundreds of carts
      // and filling our backend with useless data.

      if (!cartId || cartId === OPTIMISTIC_CART_ID) {
        await createCart()
      }

      // Retreive the updated cart id directly from the cartIdContext to ensure that we have the
      // cart id from the possibly newly created cart.

      const latestCartId = cartIdContext()

      // If the cart id is still not available, or still optimistic, something must have gone wrong
      // with creating the new cart.
      if (!latestCartId || latestCartId === OPTIMISTIC_CART_ID) {
        throw new AddToCartNoCartError()
      }

      const { errors, data } = await client.mutate({
        mutation: fetchCart ? addToAndFetchCartMutation : addToCartMutation,
        variables: {
          cartId: latestCartId,
          cartItems,
        },
        context: authorizationContext,
      })

      client.refetchQueries({
        include: ['Cart'],
      })

      errors?.some((error) => handleCartError(error, setCartId))

      return data
    },
    [
      fetchCart,
      cartId,
      cartIdContext,
      client,
      authorizationContext,
      createCart,
      setCartId,
    ],
  )
}
